package fr.frux.shared.data.local

import fr.frux.shared.domain.model.HarvestPart
import fr.frux.shared.domain.model.PlantForm
import fr.frux.shared.domain.model.Tree

/**
 * Lot d'arbres lu depuis le cache local, avec sa fraîcheur (#62). [isStale] vaut vrai quand le
 * plus ancien `cached_at` du lot dépasse [TreeCachePolicy.staleAfterMillis] ; jamais vrai sur un
 * lot vide, qui n'a rien à dater.
 */
data class CachedTrees(
    val trees: List<Tree>,
    val isStale: Boolean,
)

/**
 * Source locale du cache arbres (#60, epic #8), lue avant le réseau (stale-while-revalidate)
 * et réécrite après chaque succès réseau. Surface limitée à ce que le repository consomme (#108).
 *
 * Les appels sont synchrones sous le capot (SQLDelight) : l'appelant les exécute déjà sur le
 * dispatcher IO du repository, la suspension marque juste la frontière bloquante.
 */
interface TreeLocalDataSource {
    /**
     * Arbres du cache dans un rayon de [radiusKm] km autour du point, filtrés localement par
     * [form] et [part] (mêmes axes que la requête serveur). La saison ne se filtre pas ici :
     * le cache ne porte pas les fenêtres de maturité, l'appelant saute le cache dans ce cas.
     * Les lignes expirées ([TreeCachePolicy.evictAfterMillis]) ne sont jamais renvoyées.
     */
    suspend fun nearbyTrees(
        lat: Double,
        lon: Double,
        radiusKm: Double,
        form: PlantForm? = null,
        part: HarvestPart? = null,
    ): CachedTrees

    /** Insère ou remplace [trees] en une seule transaction : jamais de lot partiel en base. */
    suspend fun upsertAll(trees: List<Tree>)

    /**
     * Applique [TreeCachePolicy] en une seule transaction (#62) : supprime d'abord les lignes
     * expirées, puis ramène le cache sous le plafond de volume en supprimant les plus anciennes
     * restantes. Les favoris ne sont jamais supprimés par l'une ou l'autre étape.
     */
    suspend fun prune()

    /** Évince les arbres créés par [userId] (#515, ADR 0029) : masquer un compte ne doit pas
     *  attendre l'expiration du cache pour retirer ses arbres de la carte hors ligne. */
    suspend fun deleteByCreator(userId: Long)
}

/**
 * Implémentation sans stockage pour les cibles sans driver SQLite (web, cf. #59) : lectures
 * vides, écritures ignorées — le repository retombe sur un comportement réseau seul.
 */
object NoOpTreeLocalDataSource : TreeLocalDataSource {
    override suspend fun nearbyTrees(
        lat: Double,
        lon: Double,
        radiusKm: Double,
        form: PlantForm?,
        part: HarvestPart?,
    ): CachedTrees = CachedTrees(trees = emptyList(), isStale = false)

    override suspend fun upsertAll(trees: List<Tree>) = Unit

    override suspend fun prune() = Unit

    override suspend fun deleteByCreator(userId: Long) = Unit
}
